import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import {
  Mail, Copy, Check, RefreshCcw, Loader2, FileText, ChevronDown, ChevronRight,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import AdminLayout from '@/components/admin/AdminLayout';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

function AdminOutreach() {
  const [data, setData] = useState({ personalized: [], generic_markdown: '' });
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('personalized');
  const [copied, setCopied] = useState(null);
  const [expanded, setExpanded] = useState({});

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API_URL}/api/admin/outreach/templates`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setData({
        personalized: res.data?.personalized || [],
        generic_markdown: res.data?.generic_markdown || '',
      });
      // Default to first email expanded
      if ((res.data?.personalized || []).length > 0) {
        setExpanded({ [res.data.personalized[0].id]: true });
      }
    } catch {
      toast.error('Failed to load outreach templates');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const copy = async (text, key, label = 'copied') => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(key);
      toast.success(label);
      setTimeout(() => setCopied(null), 1500);
    } catch {
      toast.error('Could not copy');
    }
  };

  const toggle = (id) =>
    setExpanded((prev) => ({ ...prev, [id]: !prev[id] }));

  return (
    <AdminLayout>
      <div className="space-y-6" data-testid="admin-outreach-page">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <Mail className="h-6 w-6 text-[#00F0FF]" />
              <h1 className="text-2xl font-bold text-white">Outreach Templates</h1>
            </div>
            <p className="text-sm text-white/60 max-w-2xl">
              Copy-paste-ready cold emails for the Platform Partner lane.
              Source markdown lives in <code className="text-[#00F0FF] text-xs">/app/memory/outreach/</code> —
              edit there to update what shows here.
            </p>
          </div>
          <Button variant="outline" size="sm" onClick={load} disabled={loading}
            data-testid="admin-outreach-refresh-btn"
            className="border-white/20 text-white hover:bg-white/10">
            <RefreshCcw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>

        {/* Tabs */}
        <div className="flex items-center gap-2 border-b border-white/10">
          <TabBtn active={tab === 'personalized'} onClick={() => setTab('personalized')}
            count={data.personalized.length} label="Personalized" testid="tab-personalized" />
          <TabBtn active={tab === 'generic'} onClick={() => setTab('generic')}
            label="Generic templates" testid="tab-generic" />
        </div>

        {tab === 'personalized' && (
          <div className="space-y-3" data-testid="personalized-list">
            {loading && data.personalized.length === 0 ? (
              <div className="rounded-xl border border-white/10 p-8 text-center text-white/50">Loading…</div>
            ) : data.personalized.length === 0 ? (
              <div className="rounded-xl border border-dashed border-white/15 p-8 text-center text-white/60 text-sm">
                No personalized emails found. Add them to <br/>
                <code className="text-[#00F0FF]">/app/memory/outreach/platform-partner-outreach-personalized.md</code>
              </div>
            ) : data.personalized.map((email) => (
              <article key={email.id}
                className="rounded-xl border border-white/10 bg-white/[0.03] overflow-hidden"
                data-testid={`outreach-email-${email.id}`}>
                <button type="button" onClick={() => toggle(email.id)}
                  data-testid={`outreach-toggle-${email.id}`}
                  className="w-full flex items-center justify-between gap-3 p-4 text-left hover:bg-white/5">
                  <div className="flex items-center gap-3 min-w-0">
                    {expanded[email.id] ? <ChevronDown className="h-4 w-4 text-white/50 flex-shrink-0" /> : <ChevronRight className="h-4 w-4 text-white/50 flex-shrink-0" />}
                    <div className="min-w-0">
                      <h3 className="font-bold text-white truncate">{email.title}</h3>
                      <p className="text-xs text-white/50 truncate mt-0.5">
                        {email.subject_a || email.subject_b || '(no subject line)'}
                      </p>
                    </div>
                  </div>
                </button>

                {expanded[email.id] && (
                  <div className="px-4 pb-5 pt-1 space-y-4 border-t border-white/5">
                    {/* Subject A */}
                    {email.subject_a && (
                      <SubjectBlock label="Subject A" value={email.subject_a}
                        testid={`subject-a-${email.id}`}
                        copied={copied === `subjA-${email.id}`}
                        onCopy={() => copy(email.subject_a, `subjA-${email.id}`, 'Subject A copied')} />
                    )}
                    {email.subject_b && (
                      <SubjectBlock label="Subject B" value={email.subject_b}
                        testid={`subject-b-${email.id}`}
                        copied={copied === `subjB-${email.id}`}
                        onCopy={() => copy(email.subject_b, `subjB-${email.id}`, 'Subject B copied')} />
                    )}

                    {/* Body */}
                    <div>
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-xs uppercase tracking-wider font-bold text-white/60">Body</span>
                        <button type="button" onClick={() => copy(email.body, `body-${email.id}`, 'Body copied')}
                          data-testid={`outreach-copy-body-${email.id}`}
                          className="inline-flex items-center gap-1.5 text-xs text-white/60 hover:text-white">
                          {copied === `body-${email.id}`
                            ? <><Check className="h-3 w-3 text-[#00FF94]"/>copied</>
                            : <><Copy className="h-3 w-3"/>copy body</>}
                        </button>
                      </div>
                      <pre className="bg-black/40 border border-white/10 rounded-lg p-4 whitespace-pre-wrap font-sans text-sm text-white/85 leading-relaxed">
{email.body}
                      </pre>
                    </div>

                    {/* Combined copy: subject + body in one shot */}
                    <div className="flex items-center gap-2 flex-wrap pt-2">
                      {email.subject_a && (
                        <button type="button"
                          onClick={() => copy(`Subject: ${email.subject_a}\n\n${email.body}`, `combo-a-${email.id}`, 'Subject A + body copied')}
                          data-testid={`outreach-copy-combo-a-${email.id}`}
                          className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg bg-[#00F0FF] text-black font-semibold hover:bg-[#00F0FF]/90">
                          {copied === `combo-a-${email.id}`
                            ? <><Check className="h-3 w-3"/>copied · Subject A + body</>
                            : <><Copy className="h-3 w-3"/>Copy Subject A + body</>}
                        </button>
                      )}
                      {email.subject_b && (
                        <button type="button"
                          onClick={() => copy(`Subject: ${email.subject_b}\n\n${email.body}`, `combo-b-${email.id}`, 'Subject B + body copied')}
                          data-testid={`outreach-copy-combo-b-${email.id}`}
                          className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg bg-white/10 text-white border border-white/15 hover:bg-white/15">
                          {copied === `combo-b-${email.id}`
                            ? <><Check className="h-3 w-3 text-[#00FF94]"/>copied · Subject B + body</>
                            : <><Copy className="h-3 w-3"/>Copy Subject B + body</>}
                        </button>
                      )}
                    </div>

                    {/* Notes */}
                    {email.notes && (
                      <div className="rounded-lg bg-yellow-500/5 border border-yellow-500/20 p-3 text-xs text-yellow-200/80 leading-relaxed">
                        <span className="font-bold uppercase tracking-wider text-yellow-300/90">Notes · </span>
                        {email.notes}
                      </div>
                    )}
                  </div>
                )}
              </article>
            ))}
          </div>
        )}

        {tab === 'generic' && (
          <div className="rounded-xl border border-white/10 bg-white/[0.03] p-5" data-testid="generic-markdown">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-bold text-white inline-flex items-center gap-2">
                <FileText className="h-4 w-4 text-[#00F0FF]"/> Generic templates &amp; sequences
              </h3>
              <button type="button" onClick={() => copy(data.generic_markdown, 'generic-md', 'Markdown copied')}
                data-testid="outreach-copy-generic-btn"
                className="inline-flex items-center gap-1.5 text-xs text-white/60 hover:text-white px-2.5 py-1 rounded border border-white/15">
                {copied === 'generic-md'
                  ? <><Check className="h-3 w-3 text-[#00FF94]"/>copied</>
                  : <><Copy className="h-3 w-3"/>copy all</>}
              </button>
            </div>
            <pre className="whitespace-pre-wrap font-mono text-xs text-white/75 leading-relaxed max-h-[600px] overflow-y-auto bg-black/40 border border-white/10 rounded-lg p-4">
{data.generic_markdown || '(no content)'}
            </pre>
          </div>
        )}
      </div>
    </AdminLayout>
  );
}

function TabBtn({ active, onClick, count, label, testid }) {
  return (
    <button type="button" onClick={onClick} data-testid={testid}
      className={`px-4 py-2 text-sm font-semibold transition border-b-2 -mb-[1px] ${
        active
          ? 'text-[#00F0FF] border-[#00F0FF]'
          : 'text-white/60 border-transparent hover:text-white/80'
      }`}>
      {label} {count !== undefined && <span className="text-white/40 ml-1">({count})</span>}
    </button>
  );
}

function SubjectBlock({ label, value, testid, copied, onCopy }) {
  return (
    <div>
      <div className="flex items-center justify-between mb-1.5">
        <span className="text-xs uppercase tracking-wider font-bold text-white/60">{label}</span>
        <button type="button" onClick={onCopy} data-testid={testid}
          className="inline-flex items-center gap-1 text-xs text-white/60 hover:text-white">
          {copied
            ? <><Check className="h-3 w-3 text-[#00FF94]"/>copied</>
            : <><Copy className="h-3 w-3"/>copy</>}
        </button>
      </div>
      <p className="bg-black/40 border border-white/10 rounded px-3 py-2 text-sm text-white/85 font-mono">
        {value}
      </p>
    </div>
  );
}

export default AdminOutreach;
